/**
 * Design System Styles (DSS) — type definitions
 */

export type StyleState = "normal" | "hover" | "active" | "focus";

export const STYLE_STATES: readonly StyleState[] = ["normal", "hover", "active", "focus"] as const;

export type DssBackgroundMode = "none" | "color" | "gradient" | "image" | "video";
export type DssBackgroundEffectPreset =
  | "none"
  | "gradient-shift"
  | "particle-float"
  | "blob-morph"
  | "aurora"
  | "mesh-gradient";
export type DssCssUnit = "px" | "em" | "rem" | "vw";
export type DssLineHeightUnit = "px" | "em" | "";
export type DssSpacingUnit = "px" | "em" | "rem" | "%" | "vw" | "vh";
export type DssBorderStyle =
  | "none"
  | "solid"
  | "dashed"
  | "dotted"
  | "double"
  | "groove"
  | "ridge";

export interface DssGradientStop {
  color: string;
  position: number;
}

export interface DssGradient {
  type: "linear" | "radial" | "conic";
  angle: number;
  radialShape: "ellipse" | "circle";
  radialCenterX: number;
  radialCenterY: number;
  stops: readonly DssGradientStop[];
}

export interface DssBackgroundImage {
  url: string;
  positionX: number;
  positionY: number;
  size: "cover" | "contain" | "auto" | "custom";
  repeat: "no-repeat" | "repeat" | "repeat-x" | "repeat-y";
  attachment: "scroll" | "fixed" | "local";
  customWidth: string;
  customHeight: string;
}

export interface DssBackgroundVideo {
  url: string;
  autoplay: boolean;
  loop: boolean;
  muted: boolean;
}

export interface DssBackgroundEffect {
  preset: DssBackgroundEffectPreset;
  intensity: number;
  speed: number;
  color: string;
  accentColor: string;
  particleCount: number;
  blendMode: "normal" | "screen" | "overlay" | "soft-light";
}

export interface DssBackground {
  mode: DssBackgroundMode;
  color: string;
  opacity: number;
  gradient: DssGradient;
  image: DssBackgroundImage;
  video: DssBackgroundVideo;
  effect: DssBackgroundEffect;
}

export interface DssTypography {
  fontFamily: string;
  fontSize: number;
  fontSizeUnit: DssCssUnit;
  fontWeight: number;
  fontStyle: "normal" | "italic";
  textTransform: "none" | "uppercase" | "lowercase" | "capitalize";
  textDecoration: "none" | "underline" | "line-through" | "overline";
  lineHeight: number;
  lineHeightUnit: DssLineHeightUnit;
  letterSpacing: number;
  letterSpacingUnit: "px" | "em";
  wordSpacing: number;
}

export interface DssSpacingValue {
  top: number;
  right: number;
  bottom: number;
  left: number;
  unit: DssSpacingUnit;
}

export interface DssSpacing {
  padding: DssSpacingValue;
  margin: DssSpacingValue;
}

export interface DssBorder {
  style: DssBorderStyle;
  widthTop: number;
  widthRight: number;
  widthBottom: number;
  widthLeft: number;
  isWidthLinked: boolean;
  color: string;
  radiusTopLeft: number;
  radiusTopRight: number;
  radiusBottomRight: number;
  radiusBottomLeft: number;
  isRadiusLinked: boolean;
}

export interface DssText {
  color?: string;
  align?: "left" | "center" | "right" | "justify";
}

export interface DssTransform {
  rotate?: string;
  scale?: string;
  scaleX?: string;
  scaleY?: string;
  translateX?: string;
  translateY?: string;
  skewX?: string;
  skewY?: string;
  transformOrigin?: string;
}

export interface DssFilter {
  blur?: string;
  brightness?: string;
  contrast?: string;
  grayscale?: string;
  hueRotate?: string;
  invert?: string;
  opacity?: string;
  saturate?: string;
  sepia?: string;
  mixBlendMode?: string;
}

export interface DssPosition {
  display?: string;
  position?: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  zIndex?: string | number;
}

export interface DssTransition {
  property?: string;
  duration?: string;
  easing?: string;
}

export interface DssVisibility {
  hideOnDesktop?: boolean;
  hideOnTablet?: boolean;
  hideOnMobile?: boolean;
}

export interface DssResponsive {
  tablet?: Partial<DssStyles>;
  mobile?: Partial<DssStyles>;
}

export interface DssShadowConfig {
  box?: string;
  text?: string;
}

export type DssScrollDirection = "up" | "down";
export type DssRotateDirection = "cw" | "ccw";
export type DssScaleDirection = "grow" | "shrink";
export type DssFadeDirection = "in" | "out";
export type {
  DssHoverPresetId,
} from "./hover-presets.js";
import type { DssHoverPresetId } from "./hover-presets.js";
export type DssEntranceType =
  | "none"
  | "fade-in"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right"
  | "scale-up"
  | "zoom-in"
  | "pop-in"
  | "rotate-in"
  | "rotate-in-left"
  | "rotate-in-right"
  | "flip-x"
  | "flip-y"
  | "bounce-up"
  | "skew-in";

export interface DssVerticalScrollEffect {
  enabled: boolean;
  speed: number;
  direction: DssScrollDirection;
}

export interface DssHorizontalScrollEffect {
  enabled: boolean;
  speed: number;
}

export interface DssTransparencyEffect {
  enabled: boolean;
  direction: DssFadeDirection;
  viewportStart: number;
  viewportEnd: number;
}

export interface DssBlurEffect {
  enabled: boolean;
  maxBlur: number;
  viewportStart: number;
  viewportEnd: number;
}

export interface DssRotateEffect {
  enabled: boolean;
  direction: DssRotateDirection;
  speed: number;
  viewportStart: number;
  viewportEnd: number;
}

export interface DssScaleEffect {
  enabled: boolean;
  direction: DssScaleDirection;
  rangeMin: number;
  rangeMax: number;
  viewportStart: number;
  viewportEnd: number;
}

export interface DssScrollEffects {
  vertical: DssVerticalScrollEffect;
  horizontal: DssHorizontalScrollEffect;
  transparency: DssTransparencyEffect;
  blur: DssBlurEffect;
  rotate: DssRotateEffect;
  scale: DssScaleEffect;
}

export interface DssAnimation {
  entrance?: DssEntranceType;
  entranceType?: DssEntranceType;
  hoverPreset?: DssHoverPresetId;
  durationMs?: number;
  delayMs?: number;
  easing?: "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear";
  hoverScale?: number;
  hoverTranslateY?: number;
  hoverShadow?: string;
  scrollThreshold?: number;
  scrollEffects?: DssScrollEffects;
  transition?: string;
  transform?: string;
  duration?: string;
}

export interface DssStyles {
  background?: DssBackground | null;
  typography?: DssTypography;
  spacing?: DssSpacing;
  border?: DssBorder;
  shadow?: string | DssShadowConfig;
  text?: DssText;
  opacity?: string;
  animation?: DssAnimation;
  transform?: DssTransform;
  filter?: DssFilter;
  position?: DssPosition;
  responsive?: DssResponsive;
  transition?: DssTransition;
  visibility?: DssVisibility;
  states?: Partial<Record<StyleState, Partial<DssStyles>>>;
}

export interface DssCssStyles {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  letterSpacing?: string;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  borderStyle?: string;
  padding?: string;
  margin?: string;
  opacity?: string;
  boxShadow?: string;
  textAlign?: string;
  textDecoration?: string;
  textTransform?: string;
  transition?: string;
  transform?: string;
}

export interface DssBlockStyles {
  states: Partial<Record<StyleState, Partial<DssStyles>>>;
}

export interface DssComputedFragment {
  inlineStyles: Record<string, string>;
  cssRules: string;
  wrapperAttrs: Record<string, string>;
}

export interface DssComputedOutput extends DssComputedFragment {
  scopedClass: string;
}
